import { useCallback, useMemo } from 'react';

import {
  SharedMeetingDto,
  useCreateSharedMeeting,
  useDeleteSharedMeeting,
  useGetSharedMeeting,
  useGetSharedMeetingData,
  usePatchSharedMeeting
} from '@/api/generated';

const useSharedMeeting = (
  meetingId?: string,
  sharedMeetingId?: string,
  calendarEventId?: string
) => {
  const {
    data: sharedMeetingResponse,
    refetch: refetchSharedMeeting,
    isLoading: sharedMeetingLoading
  } = useGetSharedMeeting(
    { meetingId, calendarEventId },
    {
      query: {
        queryKey: ['getSharedMeeting', meetingId, calendarEventId],
        enabled: !!meetingId || !!calendarEventId
      }
    }
  );

  const sharedMeeting = useMemo(
    () => sharedMeetingResponse?.items?.[0] || null,
    [sharedMeetingResponse?.items]
  );

  const {
    data: dataResponse,
    refetch: refetchSharedMeetingData,
    isLoading: sharedMeetingDataLoading
  } = useGetSharedMeetingData(sharedMeetingId!, {
    query: {
      queryKey: ['getSharedMeetingData', sharedMeetingId],
      enabled: !!sharedMeetingId,
      refetchOnWindowFocus: false
    }
  });

  const sharedMeetingData = useMemo(() => dataResponse?.items?.[0] || null, [dataResponse?.items]);

  const { mutateAsync: mutateCreateSharedMeeting } = useCreateSharedMeeting();
  const { mutateAsync: mutateDeleteSharedMeeting } = useDeleteSharedMeeting();
  const { mutateAsync: mutatePatchSharedMeeting } = usePatchSharedMeeting();

  const createSharedMeeting = useCallback(
    async (sharedMeeting: SharedMeetingDto) => {
      const response = await mutateCreateSharedMeeting({ data: sharedMeeting });
      return response?.items?.[0];
    },
    [mutateCreateSharedMeeting]
  );

  const patchSharedMeeting = useCallback(
    async (id: string, sharedMeeting: SharedMeetingDto) => {
      await mutatePatchSharedMeeting({ id, data: sharedMeeting });
    },
    [mutatePatchSharedMeeting]
  );

  const deleteSharedMeeting = useCallback(
    async (id: string) => {
      await mutateDeleteSharedMeeting({ id });
    },
    [mutateDeleteSharedMeeting]
  );

  return {
    sharedMeeting,
    sharedMeetingLoading,
    refetchSharedMeeting,
    createSharedMeeting,
    patchSharedMeeting,
    deleteSharedMeeting,
    sharedMeetingData
  };
};

export default useSharedMeeting;
